import browserCookie from 'js-cookie';

import type { BrowserCookie, Consent } from '@personly/libs-types';

export interface CookieAttributes {
  /**
   * Define when the cookie will be removed. Value can be a Number
   * which will be interpreted as days from time of creation or a
   * Date instance. If omitted, the cookie becomes a session cookie.
   */
  expires?: number | Date | undefined;

  /**
   * Define the path where the cookie is available. Defaults to '/'
   */
  path?: string | undefined;

  /**
   * Define the domain where the cookie is available. Defaults to
   * the domain of the page where the cookie was created.
   */
  domain?: string | undefined;

  /**
   * A Boolean indicating if the cookie transmission requires a
   * secure protocol (https). Defaults to false.
   */
  secure?: boolean | undefined;

  /**
   * Asserts that a cookie must not be sent with cross-origin requests,
   * providing some protection against cross-site request forgery
   * attacks (CSRF)
   */
  sameSite?: 'strict' | 'Strict' | 'lax' | 'Lax' | 'none' | 'None' | undefined;
}

class Cookies {
  cookies: BrowserCookie[];
  hostname: string;

  constructor(hostname: string, cookies: BrowserCookie[]) {
    this.hostname = hostname;
    this.cookies = cookies;
  }

  setCookie(consent: Consent, name: string, data: string) {
    const cookie = this.cookies.find(
      (cookie) => cookie.name === name && cookie.domains[this.hostname]
    );

    if (!cookie) return {};

    if (!consent.types.includes(cookie.typeHandle)) return {};

    const options: CookieAttributes = {};
    if (cookie.expires && cookie.expires !== '0') {
      options.expires = parseInt(cookie.expires);
    }

    if (cookie.groupHandle === 'DOMAIN')
      options.domain = cookie.domains[this.hostname];
    else if (cookie.groupHandle === 'HOST') options.domain = this.hostname;

    browserCookie.set(name, data, options);

    return options;
  }

  getCookie(consent: Consent, name: string) {
    const cookie = this.cookies.find(
      (cookie) => cookie.name === name && cookie.domains[this.hostname]
    );

    if (!cookie) return null;

    if (!consent.types.includes(cookie.typeHandle)) return null;

    return browserCookie.get(name);
  }
}

export default Cookies;
